/**
 * Module overview — /settings/custom-modules/$key
 *
 * Shows the module's metadata and lets tenant admins edit the name,
 * description, icon, and status. The module key is immutable because
 * it's used as a URL segment and in permission grants.
 *
 * Also has a Delete button that cascades through all entities,
 * records, workflows, etc. — this is destructive and requires a
 * confirm dialog.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/')({
  component: ModuleOverviewPage,
})

function ModuleOverviewPage() {
  const { key } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: module, isLoading } = useQuery(
    orpc.modules.custom.modules.get.queryOptions({ input: { key } }),
  )

  // Track whether the user has made local edits. Before the first edit,
  // form values are derived directly from the query result so a refresh
  // always shows the server's truth. After the user touches a field, we
  // switch to local state so their edits don't get overwritten by a
  // background refetch.
  const [dirty, setDirty] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [icon, setIcon] = useState('')
  const [status, setStatus] = useState<'active' | 'paused' | 'disabled'>('active')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const lastModuleId = useRef<string | null>(null)

  // Sync form state from server data when: (a) the module first loads,
  // (b) the module identity changes, or (c) the form is not dirty (so
  // a successful save + refetch shows the new value).
  useEffect(() => {
    if (module && (!dirty || lastModuleId.current !== module.id)) {
      setName(module.name)
      setDescription(module.description ?? '')
      setIcon(module.icon ?? '')
      const s = String(module.status)
      setStatus(
        s === 'active' || s === 'paused' || s === 'disabled' ? s : 'active',
      )
      lastModuleId.current = module.id
      setDirty(false)
    }
  }, [module, dirty])

  // Wrap setters to mark dirty
  const edit = <T,>(setter: (v: T) => void) => (v: T) => { setDirty(true); setter(v) }

  const updateMutation = useMutation(
    orpc.modules.custom.modules.update.mutationOptions({
      onSuccess: () => {
        toast.success('Module updated')
        setDirty(false)
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.modules.get.queryKey({ input: { key } }),
        })
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.modules.list.queryKey(),
        })
      },
      onError: (err) => toast.error(err.message || 'Failed to update module'),
    }),
  )

  const deleteMutation = useMutation(
    orpc.modules.custom.modules.delete.mutationOptions({
      onSuccess: () => {
        toast.success('Module deleted')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.modules.list.queryKey(),
        })
        void navigate({ to: '/settings/custom-modules' })
      },
      onError: (err) => toast.error(err.message || 'Failed to delete module'),
    }),
  )

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!module) {
    return <p className="text-muted-foreground">Module not found.</p>
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    updateMutation.mutate({
      key: module.key,
      name,
      description: description || undefined,
      icon: icon || undefined,
      status,
    })
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Module settings</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label htmlFor="key-display">Key</Label>
              <Input id="key-display" value={module.key} disabled />
              <p className="text-xs text-muted-foreground mt-1">
                The key is immutable. To rename, delete and recreate.
              </p>
            </div>

            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => edit(setName)(e.target.value)} required />
            </div>

            <div>
              <Label htmlFor="icon">Icon</Label>
              <Input
                id="icon"
                value={icon}
                onChange={(e) => edit(setIcon)(e.target.value)}
                placeholder="Box"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => edit(setDescription)(e.target.value)}
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="status">
                Status
                {module.status !== status && (
                  <span className="ml-2 text-[10px] text-muted-foreground font-normal">
                    (server: {module.status})
                  </span>
                )}
              </Label>
              <Select
                key={module.id + status}
                defaultValue={status}
                onValueChange={edit(setStatus) as (v: string) => void}
              >
                <SelectTrigger id="status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Paused modules stay in the sidebar but reject writes. Disabled modules are
                hidden entirely.
              </p>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive">Danger zone</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium">Delete this module</p>
              <p className="text-sm text-muted-foreground">
                Permanently removes the module, all its entities, all records, all workflows,
                and all webhooks. This cannot be undone.
              </p>
            </div>
            <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{module.name}"?</DialogTitle>
            <DialogDescription>
              This will permanently delete the module and ALL its data (entities, records,
              workflows, webhooks). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate({ key: module.key })}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
